/**
 * Client-side Dutch auction price computation for UGM v2.2.
 *
 * Mirrors `UnifiedGridManagerV22._dutchPrice` so the UI can render a
 * live decaying clearing price without round-tripping `dutchAuctionPrice`
 * to the chain on every animation frame. The onchain value remains the
 * source of truth — these helpers exist purely for animation timing,
 * "you'd pay ~X" tooltips, and progress bars.
 *
 * Decay is linear:
 *   - At `forfeitedAt` (seconds since epoch) the price is `seatPrice`.
 *   - At `forfeitedAt + forfeitureDuration` and beyond, the price is `0`.
 *   - In between, the price scales proportionally to time remaining.
 *
 * For grids that aren't V22 or for seats that aren't in a Dutch
 * auction, callers should not invoke these helpers. See the rest of
 * the V22 surface in `PLAN_UGM_V22_APP_INDEXER.md` §4.5.
 */

export interface DutchAuctionParams {
  /** The seat's preserved `selfAssessedPrice` at the moment of forfeit. */
  seatPrice: bigint;
  /** Unix-seconds timestamp of the onchain forfeit. `0` ⇒ live seat. */
  forfeitedAt: number;
  /** Decay window in seconds (V22 GridConfig.forfeitureDuration). */
  forfeitureDuration: number;
  /** Unix-seconds timestamp at which to evaluate the curve. */
  nowSeconds: number;
}

/**
 * Returns the live Dutch auction clearing price for a forfeited V22 seat.
 *
 * Returns `0n` for live seats (`forfeitedAt == 0`), seats whose decay
 * window has expired, or invalid inputs (`forfeitureDuration <= 0`).
 * The contract's `_claimVacantSeat` requires `newDeposit > 0` even when
 * the clearing price hits zero — UI buttons should reflect that.
 */
export function getDutchPrice({
  seatPrice,
  forfeitedAt,
  forfeitureDuration,
  nowSeconds,
}: DutchAuctionParams): bigint {
  if (seatPrice <= 0n) return 0n;
  if (!forfeitedAt || forfeitedAt <= 0) return 0n;
  if (forfeitureDuration <= 0) return 0n;

  const elapsed = nowSeconds - forfeitedAt;
  if (elapsed <= 0) return seatPrice;
  if (elapsed >= forfeitureDuration) return 0n;

  // Mirror the contract's discrete-second linear decay:
  //   priceLeft = seatPrice * (forfeitureDuration - elapsed) / forfeitureDuration
  // Use bigint math so we don't lose precision on 18-decimal token amounts.
  const remaining = BigInt(forfeitureDuration - elapsed);
  const window = BigInt(forfeitureDuration);
  return (seatPrice * remaining) / window;
}

/**
 * Fraction of the Dutch decay window already consumed (in `[0, 1]`).
 *
 * Useful for rendering progress bars / countdown rings. Returns `1`
 * for expired windows (price has reached zero) and `0` for live seats.
 */
export function getDutchProgress(
  forfeitedAt: number,
  forfeitureDuration: number,
  nowSeconds: number,
): number {
  if (!forfeitedAt || forfeitedAt <= 0) return 0;
  if (forfeitureDuration <= 0) return 1;
  const elapsed = nowSeconds - forfeitedAt;
  if (elapsed <= 0) return 0;
  if (elapsed >= forfeitureDuration) return 1;
  return elapsed / forfeitureDuration;
}

/**
 * Unix-seconds timestamp at which a Dutch auction's clearing price hits
 * zero. Returns `null` for live seats. Useful for the seat-data
 * builder, which previously wrote `antiSnipeEndsAt` from the V21
 * 5-minute window — V22 uses this longer window instead.
 */
export function getDutchEndsAt(
  forfeitedAt: number,
  forfeitureDuration: number,
): number | null {
  if (!forfeitedAt || forfeitedAt <= 0) return null;
  if (forfeitureDuration <= 0) return null;
  return forfeitedAt + forfeitureDuration;
}
